import { useEffect, useMemo, useState } from "react";
import {
  Page,
  Card,
  Text,
  Badge,
  Banner,
  BlockStack,
  InlineStack,
  Button,
  Modal,
  Select,
  DropZone,
  Divider,
  Spinner,
} from "@shopify/polaris";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatDate } from "../../utility/funcs";
import { parsePaseCsv, groupRuns, proposeRuns, movementWarnings, destKey, tagLabel } from "../../utility/pase";

// TAG PASE crossings → traslados. Staff upload the per-tag CSV exports from
// apps.pase.com.mx; each tag's pending crossings are grouped into runs and the
// page proposes the traslado each run belongs to. Nothing is written until
// "Aceptar" (pase_link), which adds the tolls as GoMove-paid peaje expenses.
// PASE invoices each tag once a month, so these count as invoiced: no CFDI to
// chase per toll. Runs with no traslado (own-staff trips like Laredo, unit
// swaps) are marked "Sin traslado (staff)".

const ORDER_FIELDS =
  "id, status, started_at, scheduled_at, finished_at, trasladista, trasladista_secondary, origin_label, dest_label, pase_tag, car(plate)";
const USABLE = ["authorized", "in_progress", "completed", "settled"];

const fmtTime = (iso) =>
  new Date(iso).toLocaleString("es-MX", {
    timeZone: "America/Mexico_City",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
const short = (s) => (s || "").replace(/^(.)(.*)$/, (_, a, b) => a + b.toLowerCase());
const orderLabel = (o) =>
  `#${o.id} · ${o.origin_label || "?"} → ${o.dest_label || "?"} · ${o.car?.plate || ""} · ${formatDate(
    o.started_at || o.scheduled_at,
  )}`;

function CasetasTag() {
  const { hasPermission } = UserAuth();
  const canEdit = hasPermission("services.create");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pending, setPending] = useState([]);
  const [parked, setParked] = useState([]);
  const [history, setHistory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cash, setCash] = useState([]);
  const [lastMove, setLastMove] = useState(null);
  const [tags, setTags] = useState({});
  const [choice, setChoice] = useState({});
  const [expanded, setExpanded] = useState({});
  const [showParked, setShowParked] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const load = async () => {
    setLoading(true);
    const [{ data: pend, error: e1 }, { data: park }, { data: hist }, { data: last }, { data: tagRows }] = await Promise.all([
      supabase.from("pase_movements").select("*").eq("kind", "toll").eq("status", "pending").order("moved_at"),
      supabase
        .from("pase_movements")
        .select("*")
        .eq("kind", "toll")
        .in("status", ["staff", "ignored"])
        .order("moved_at", { ascending: false })
        .limit(300),
      supabase
        .from("pase_movements")
        .select("tag, caseta_no, clase, amount, kind, status, traslado_order, order:traslado_orders(trasladista, dest_label)")
        .eq("kind", "toll")
        .limit(10000),
      supabase.from("pase_movements").select("moved_at").order("moved_at", { ascending: false }).limit(1).maybeSingle(),
      supabase.from("pase_tags").select("*"),
    ]);
    setTags(Object.fromEntries((tagRows || []).map((x) => [x.tag, x])));
    if (e1) {
      setError(e1.message);
      setLoading(false);
      return;
    }
    let ords = [];
    let tickets = [];
    if (pend.length) {
      const from = new Date(new Date(pend[0].moved_at).getTime() - 5 * 86400000).toISOString();
      const to = new Date(new Date(pend[pend.length - 1].moved_at).getTime() + 86400000).toISOString();
      const { data: o, error: e2 } = await supabase
        .from("traslado_orders")
        .select(ORDER_FIELDS)
        .in("status", USABLE)
        .or(`and(started_at.gte.${from},started_at.lte.${to}),and(started_at.is.null,scheduled_at.gte.${from},scheduled_at.lte.${to})`)
        .order("started_at");
      if (e2) setError(e2.message);
      ords = o || [];
      if (ords.length) {
        const { data: t } = await supabase
          .from("traslado_tickets")
          .select("traslado_order, total, ticket_date")
          .eq("kind", "peaje")
          .in("traslado_order", ords.map((x) => x.id));
        tickets = t || [];
      }
    }
    setPending(pend);
    setParked(park || []);
    setHistory(hist || []);
    setOrders(ords);
    setCash(tickets);
    setLastMove(last?.moved_at || null);
    setChoice({});
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const runs = useMemo(() => {
    const tagDrivers = {};
    const linkedTags = {};
    const routes = {};
    history
      .filter((m) => m.status === "linked" && m.traslado_order)
      .forEach((m) => {
        if (m.order?.trasladista != null) (tagDrivers[m.tag] = tagDrivers[m.tag] || new Set()).add(m.order.trasladista);
        (linkedTags[m.traslado_order] = linkedTags[m.traslado_order] || new Set()).add(m.tag);
        const k = destKey(m.order?.dest_label);
        if (k) (routes[k] = routes[k] || new Set()).add(m.caseta_no);
      });
    return proposeRuns(groupRuns(pending), orders, tagDrivers, linkedTags, routes).map((r) => ({
      ...r,
      warnings: movementWarnings(
        r,
        history,
        r.proposed ? cash.filter((c) => c.traslado_order === r.proposed.id) : [],
      ),
    }));
  }, [pending, orders, history, cash]);

  const parkedRuns = useMemo(() => groupRuns(parked).reverse(), [parked]);

  const selectedOrder = (r) => {
    const v = choice[r.key];
    if (v === undefined) return r.proposed?.id ?? null;
    return v === "" ? null : Number(v);
  };

  const act = async (r, fn) => {
    setBusy(r.key);
    setError(null);
    const { error: err } = await fn();
    setBusy(null);
    if (err) setError(err.message);
    else load();
  };
  const accept = (r) =>
    act(r, () => supabase.rpc("pase_link", { p_order: selectedOrder(r), p_ids: r.movements.map((m) => m.id) }));
  const park = (r, status) =>
    act(r, () => supabase.rpc("pase_set_status", { p_ids: r.movements.map((m) => m.id), p_status: status }));

  // ------------------------------------------------------------------ upload
  const closeUpload = () => {
    setUploadOpen(false);
    setFiles([]);
    setUploadResult(null);
    setUploadError(null);
  };
  const upload = async () => {
    setUploading(true);
    setUploadError(null);
    try {
      const rows = [];
      for (const f of files) {
        // PASE exports are Latin-1.
        const text = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsText(f, "ISO-8859-1");
        });
        try {
          rows.push(...parsePaseCsv(text));
        } catch (e) {
          throw new Error(`${f.name}: ${e.message}`);
        }
      }
      if (!rows.length) throw new Error("Los archivos no tienen movimientos");
      const { data, error: err } = await supabase.rpc("pase_import", { p_rows: rows });
      if (err) throw err;
      setUploadResult(data);
      setFiles([]);
      load();
    } catch (e) {
      setUploadError(e.message || "No se pudo cargar");
    } finally {
      setUploading(false);
    }
  };

  // -------------------------------------------------------------------- view
  const orderOptions = (r) => {
    const ids = new Set(r.candidates.map((c) => c.order.id));
    return [
      { label: "Elegir traslado…", value: "" },
      ...r.candidates.map((c) => ({ label: orderLabel(c.order), value: String(c.order.id) })),
      ...orders.filter((o) => !ids.has(o.id)).map((o) => ({ label: `${orderLabel(o)} (fuera de horario)`, value: String(o.id) })),
    ];
  };

  const crossingList = (r) => (
    <BlockStack gap="100">
      {r.movements.map((m) => (
        <InlineStack key={m.id} align="space-between" blockAlign="start" wrap={false} gap="200">
          <BlockStack gap="0">
            <Text>
              {fmtTime(m.moved_at)} · {short(m.caseta)}
            </Text>
            {r.warnings?.[m.id]?.map((w) => (
              <Text key={w} tone="caution" variant="bodySm">
                {w}
              </Text>
            ))}
          </BlockStack>
          <Text>{formatPrice(m.amount)}</Text>
        </InlineStack>
      ))}
    </BlockStack>
  );

  const runHeader = (r) => (
    <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
      <BlockStack gap="0">
        <Text fontWeight="semibold">
          {tags[r.tag] ? tagLabel(tags[r.tag]) : r.tag} · {fmtTime(r.start)} → {fmtTime(r.end)}
        </Text>
        <Text tone="subdued" variant="bodySm">
          {r.movements.length} casetas · {short(r.movements[0].caseta)} → {short(r.movements[r.movements.length - 1].caseta)}
        </Text>
      </BlockStack>
      <Text fontWeight="semibold">{formatPrice(r.total)}</Text>
    </InlineStack>
  );

  return (
    <AppWrapper>
      <Page
        title="Casetas TAG"
        subtitle="Movimientos PASE ligados a traslados"
        backAction={{ url: "/vehicle_transfers" }}
        primaryAction={canEdit ? { content: "Cargar CSV", onAction: () => setUploadOpen(true) } : undefined}
      >
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}
          {loading ? (
            <Spinner size="small" />
          ) : runs.length === 0 ? (
            <Card>
              <InlineStack gap="200" blockAlign="center">
                <Badge tone="success">Al día</Badge>
                <Text tone="subdued">
                  {lastMove
                    ? `Todas las casetas están clasificadas · último movimiento cargado ${fmtTime(lastMove)}`
                    : "Aún no se ha cargado ningún movimiento"}
                </Text>
              </InlineStack>
            </Card>
          ) : (
            runs.map((r) => {
              const sel = selectedOrder(r);
              const nWarn = Object.keys(r.warnings || {}).length;
              return (
                <Card key={r.key}>
                  <BlockStack gap="300">
                    {runHeader(r)}
                    <Divider />
                    <Select
                      label="Traslado"
                      options={orderOptions(r)}
                      value={sel == null ? "" : String(sel)}
                      onChange={(v) => setChoice((c) => ({ ...c, [r.key]: v }))}
                      helpText={r.why}
                      disabled={!canEdit}
                    />
                    {nWarn > 0 && (
                      <Text tone="caution">
                        {nWarn === 1 ? "1 caseta a revisar" : `${nWarn} casetas a revisar`} (ver detalle)
                      </Text>
                    )}
                    {expanded[r.key] && crossingList(r)}
                    <InlineStack align="space-between" blockAlign="center">
                      <Button variant="plain" onClick={() => setExpanded((x) => ({ ...x, [r.key]: !x[r.key] }))}>
                        {expanded[r.key] ? "Ocultar casetas" : "Ver casetas"}
                      </Button>
                      {canEdit && (
                        <InlineStack gap="200">
                          <Button disabled={!!busy} onClick={() => park(r, "ignored")} variant="plain" tone="critical">
                            Ignorar
                          </Button>
                          <Button disabled={!!busy} onClick={() => park(r, "staff")}>
                            Sin traslado (staff)
                          </Button>
                          <Button
                            variant="primary"
                            disabled={!!busy || sel == null}
                            loading={busy === r.key}
                            onClick={() => accept(r)}
                          >
                            {sel == null ? "Aceptar" : `Ligar a #${sel}`}
                          </Button>
                        </InlineStack>
                      )}
                    </InlineStack>
                  </BlockStack>
                </Card>
              );
            })
          )}

          {parkedRuns.length > 0 && (
            <InlineStack>
              <Button variant="plain" onClick={() => setShowParked((s) => !s)}>
                {showParked ? "Ocultar casetas sin traslado" : `Casetas sin traslado (${parkedRuns.length} recorridos)`}
              </Button>
            </InlineStack>
          )}
          {showParked &&
            parkedRuns.map((r) => (
              <Card key={r.key}>
                <BlockStack gap="200">
                  {runHeader(r)}
                  <InlineStack align="space-between" blockAlign="center">
                    <Badge>{r.movements[0].status === "staff" ? "Staff" : "Ignorada"}</Badge>
                    {canEdit && (
                      <Button variant="plain" disabled={!!busy} onClick={() => park(r, "pending")}>
                        Volver a pendientes
                      </Button>
                    )}
                  </InlineStack>
                </BlockStack>
              </Card>
            ))}
        </BlockStack>

        <Modal
          open={uploadOpen}
          onClose={closeUpload}
          title="Cargar movimientos PASE"
          primaryAction={{ content: "Cargar", onAction: upload, loading: uploading, disabled: !files.length }}
          secondaryActions={[{ content: "Cerrar", onAction: closeUpload }]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text tone="subdued">
                En apps.pase.com.mx exporta los movimientos de cada TAG. Puedes subir varios a la vez; los que ya
                estaban se omiten.
              </Text>
              {uploadError && <Banner tone="critical">{uploadError}</Banner>}
              <DropZone
                accept=".csv,text/csv"
                allowMultiple
                onDrop={(_all, accepted) =>
                  setFiles((prev) => [...prev, ...accepted.filter((f) => !prev.some((p) => p.name === f.name))])
                }
              >
                {files.length ? (
                  <div style={{ padding: 12 }}>
                    <BlockStack gap="100">
                      {files.map((f) => (
                        <InlineStack key={f.name} align="space-between" blockAlign="center">
                          <Text>{f.name}</Text>
                          <Button
                            variant="plain"
                            tone="critical"
                            onClick={() => setFiles((prev) => prev.filter((p) => p.name !== f.name))}
                          >
                            Quitar
                          </Button>
                        </InlineStack>
                      ))}
                    </BlockStack>
                  </div>
                ) : (
                  <DropZone.FileUpload actionHint="Archivos .csv de PASE" />
                )}
              </DropZone>
              {uploadResult && (
                <Banner tone="success">
                  {`${uploadResult.inserted} movimientos nuevos`}
                  {uploadResult.skipped ? ` · ${uploadResult.skipped} ya estaban` : ""}
                  {uploadResult.adopted ? ` · ${uploadResult.adopted} ya capturados en su traslado` : ""}
                </Banner>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default CasetasTag;
